import {
  Alert,
  Box,
  Dialog,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  useTheme,
} from "@mui/material";
import React from "react";
import { useParams } from "react-router";

import { useAuth, useNavigation } from "@/providers";
import {
  MaskedSecretValue,
  PageLayout,
  RobotoButton,
  RobotoLoadingButton,
  RobotoTypography,
} from "@/shared/components";
import { RobotoPrincipalUnderlineLink } from "@/shared/components/principal";
import { FormattedDateTime } from "@/shared/datetime";
import { useDeleteSecretMutation } from "@/shared/domain/secrets/hooks/useDeleteSecretMutation";
import { useSecretRecord } from "@/shared/domain/secrets/hooks/useSecretRecord";
import { RobotoDomainException } from "@/shared/http";

export const Secrets: React.FC = () => {
  const { secretName } = useParams();
  const { currentOrganization } = useAuth();
  const theme = useTheme();
  const { goto } = useNavigation();

  const secretQuery = useSecretRecord(
    secretName || "",
    currentOrganization?.org_id || "",
  );

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);

  const deleteSecretMutation = useDeleteSecretMutation();

  const handleDeleteSecret = () => {
    deleteSecretMutation.mutate(
      {
        secretName: secretQuery.data?.name || "",
        orgId: currentOrganization?.org_id || "",
      },
      {
        onSuccess: () => {
          goto.secretsList();
        },
      },
    );
  };

  return (
    <PageLayout
      title={"Roboto - Secret Details"}
      headerContentLeft={"Secret Details"}
    >
      {secretQuery.isPending && (
        <Skeleton
          variant="rounded"
          height={100}
          sx={{ marginTop: theme.spacing(2) }}
        />
      )}

      {!secretQuery.isPending && secretQuery.data && (
        <List
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            padding: 0,
          }}
        >
          <ListItem
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <ListItemText primary="Secret name" />
            <RobotoTypography>{secretName}</RobotoTypography>
          </ListItem>

          <ListItem
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <ListItemText primary="Secret value" />
            <Box sx={{ marginTop: theme.spacing(1) }}>
              <MaskedSecretValue />
            </Box>
            <RobotoTypography variant="caption">
              Secret values cannot be viewed or changed in the web app. To
              rotate this secret, delete it and create a new one, or overwrite
              its value with the Roboto CLI (roboto secrets write).
            </RobotoTypography>
          </ListItem>

          <ListItem
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <ListItemText primary="Created" />
            <RobotoTypography>
              <FormattedDateTime value={secretQuery.data?.created} />
            </RobotoTypography>
          </ListItem>

          <ListItem
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <ListItemText primary="Created by" />
            <RobotoPrincipalUnderlineLink
              principal={secretQuery.data?.created_by || ""}
            />
          </ListItem>

          <ListItem
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <ListItemText primary="Modified" />
            <RobotoTypography>
              <FormattedDateTime value={secretQuery.data?.modified} />
            </RobotoTypography>
          </ListItem>

          <ListItem
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <ListItemText primary="Modified by" />
            <RobotoPrincipalUnderlineLink
              principal={secretQuery.data?.modified_by || ""}
            />
          </ListItem>
          <RobotoButton
            eventName={"DeleteSecretClicked"}
            variant={"contained"}
            color="error"
            sx={{ marginTop: theme.spacing(2), marginLeft: theme.spacing(2) }}
            onClick={() => {
              setIsDeleteDialogOpen(true);
            }}
          >
            Delete Secret
          </RobotoButton>
        </List>
      )}

      {secretQuery.isError && (
        <Alert severity="error" sx={{ marginTop: theme.spacing(2) }}>
          {secretQuery.error instanceof RobotoDomainException
            ? secretQuery.error.message
            : "Failed to load secret. Please try refreshing the page."}
        </Alert>
      )}

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
        }}
      >
        <Box sx={{ padding: theme.spacing(2) }}>
          <DialogTitle>Delete Secret</DialogTitle>
          <RobotoTypography
            sx={{ marginLeft: theme.spacing(3), marginRight: theme.spacing(3) }}
          >
            Are you sure you want to delete this secret? This cannot be undone.
          </RobotoTypography>
          {deleteSecretMutation.isError && (
            <Alert
              severity="error"
              sx={{
                marginTop: theme.spacing(3),
                marginLeft: theme.spacing(3),
                marginRight: theme.spacing(3),
              }}
            >
              {deleteSecretMutation.error.message}
            </Alert>
          )}
          <RobotoLoadingButton
            loading={deleteSecretMutation.isPending}
            eventName={"DeleteSecretConfirmationClicked"}
            variant={"contained"}
            color="error"
            sx={{
              marginTop: theme.spacing(3),
              marginRight: theme.spacing(2),
              marginLeft: theme.spacing(3),
            }}
            onClick={() => {
              void handleDeleteSecret();
            }}
          >
            Delete Secret
          </RobotoLoadingButton>
          <RobotoButton
            eventName={"DeleteSecretConfirmationCanceled"}
            variant={"contained"}
            color="primary"
            sx={{ marginTop: theme.spacing(3) }}
            onClick={() => {
              setIsDeleteDialogOpen(false);
            }}
          >
            Cancel
          </RobotoButton>
        </Box>
      </Dialog>
    </PageLayout>
  );
};
